import { Switch } from "@xola/ui-kit";
import { formatDate, PurchaseItemStatus } from "common";
import { sortBy, uniq } from "lodash";
import React, { useState } from "react";
import { Permission, Purchase, Traveler, WaitlistItem, Waiver } from "types";
import {
    getMergeEvent,
    MergeTimelineItem,
    TravelerMergeAudit,
} from "@/components/Customers/Contacts/Timeline/Items/MergeTimelineItem";
import { PaymentTimelineItem } from "@/components/Customers/Contacts/Timeline/Items/PurchaseTimelineItem";
import { WaitlistTimelineItem } from "@/components/Customers/Contacts/Timeline/Items/WaitlistTimelineItem";
import { WaiverTimelineItem } from "@/components/Customers/Contacts/Timeline/Items/WaiverTimelineItem";
import { Loader } from "@/components/Loader";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";

interface ContactTimelineProps {
    purchases: Purchase[];
    contact: Traveler;
    isLoading: boolean;
}

export const ContactTimeline = ({ purchases, contact, isLoading }: ContactTimelineProps) => {
    const { isGranted } = useAuthorization();
    const seller = useSeller();
    const [showPurchases, setShowPurchases] = useState(true);
    const [showWaivers, setShowWaivers] = useState(true);

    const { data: waivers, isLoading: isLoadingWaivers } = useCollection<Waiver>(
        "waivers",
        { traveler: contact.id, unlinked: false },
        { enabled: isGranted(Permission.WAIVER_TIMELINE_VIEW), staleTime: 3000, keepPreviousData: true },
    );

    const { data: waitlists, isLoading: isLoadingArrivalWaitlists } = useAutoFetchCollection<WaitlistItem>(
        "waitlists",
        {
            organizer: contact.id,
            status: PurchaseItemStatus.WAITLIST_CREATED,
        },
        { staleTime: 1000 },
    );

    // Always fetch merge history so it remains visible after a record is unmerged.
    const { data: mergeEvents, isLoading: isLoadingMergeEvents } = useCollection<TravelerMergeAudit>(
        "audits",
        { objectClass: "traveler", objectId: contact.id, seller: seller.id },
        {
            staleTime: 3000,
            queryKey: ["users", contact.id, "audits", seller.id],
        },
    );

    const counterpartyIds = uniq((mergeEvents ?? []).flatMap((event) => getMergeEvent(event)?.counterpartyId ?? []));
    const { data: counterparties } = useCollection<Traveler>(
        "users",
        { "id[in]": counterpartyIds.join(","), "mergedInto[exists]": true, type: 2, seller: seller.id },
        { enabled: counterpartyIds.length > 0 },
    );

    if (isLoading || isLoadingWaivers || isLoadingArrivalWaitlists || isLoadingMergeEvents) {
        return <Loader />;
    }

    const travelerNames = Object.fromEntries((counterparties ?? []).map((traveler) => [traveler.id, traveler.name]));
    const mergeAudits = (mergeEvents ?? []).map((event) => ({ ...event, travelerNames }));

    const purchasesData = showPurchases ? purchases : [];
    const waiversData = showWaivers ? waivers : [];

    const sortPayload = [...purchasesData, ...waiversData, ...waitlists, ...mergeAudits];
    const changes = sortBy(sortPayload, "createdAt").reverse() ?? [];

    let currentDate = "";

    return (
        <div className="py-10">
            <div className="mb-8 flex flex-row justify-between">
                <h2>Timeline</h2>
                {waivers?.length > 0 ? (
                    <div className="space-x-4">
                        <Switch.Group>
                            <div className="flex items-center">
                                <Switch
                                    isChecked={showPurchases}
                                    size="medium"
                                    onChange={() => setShowPurchases(!showPurchases)}
                                />

                                <Switch.Label direction="left" className="ml-2">
                                    Purchases
                                </Switch.Label>
                            </div>
                        </Switch.Group>

                        <Switch.Group>
                            <div className="flex items-center">
                                <Switch
                                    isChecked={showWaivers}
                                    size="medium"
                                    onChange={() => setShowWaivers(!showWaivers)}
                                />

                                <Switch.Label direction="left" className="ml-2">
                                    Waivers
                                </Switch.Label>
                            </div>
                        </Switch.Group>
                    </div>
                ) : null}
            </div>

            {changes.map((change: any, changeIndex: number) => {
                const createdAt = formatDate(change.createdAt);
                const showDate = currentDate === "" || currentDate !== createdAt || changeIndex === 0;

                return auditTiles.map((Tile) => {
                    if (Tile.supportsAudit(change)) {
                        currentDate = createdAt;

                        // Since this Tile supports this audit, render it
                        return <Tile key={change.id} showDate={showDate} change={change} contact={contact} />;
                    }

                    return null;
                });
            })}
        </div>
    );
};

const auditTiles = [PaymentTimelineItem, WaiverTimelineItem, WaitlistTimelineItem, MergeTimelineItem];
